import React, { useState } from 'react';
import firebase from '../firebaseConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faLink,
    faPenNib,
    faEye,
    faKey,
    faClock,
    faHashtag,
    faWandMagicSparkles,
    faCopy,
    faCircleCheck,
    faCircleArrowLeft,
    faDownload,
    faEyeSlash,
} from '@fortawesome/free-solid-svg-icons';
import Toggle from './Toggle';

// Mirrors iOS ShareLinkComposerVC. Mints a tokenized one-off /s/{token}
// link with optional password / expiry / max-uses, scoped to whichever
// entity the share sheet was opened on (project or track). Lyrics/idea
// targets exist server-side but have no entry point on web yet.
const ShareLinkComposer = ({ target, project, onBack, onClose }) => {
    // Back-compat: callers that passed `project` get a project target inferred.
    const eff = target || (project ? {
        kind: 'project',
        artistId: project.artistId,
        projectId: project.id,
        trackId: null,
        name: project.name,
    } : null);
    const targetType = eff?.kind === 'track' ? 'track' : 'project';
    const [permission, setPermission] = useState('view');
    const [allowDownloads, setAllowDownloads] = useState(false);
    const [usePassword, setUsePassword] = useState(false);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [useExpiry, setUseExpiry] = useState(false);
    const [expiresAt, setExpiresAt] = useState('');
    const [useMaxUses, setUseMaxUses] = useState(false);
    const [maxUses, setMaxUses] = useState(10);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [resultUrl, setResultUrl] = useState(null);
    const [copied, setCopied] = useState(false);

    const create = async () => {
        if (busy) return;
        setBusy(true);
        setError('');
        const data = {
            artistId: eff.artistId,
            projectId: eff.projectId,
            targetType,
            permission,
            allowDownloads,
        };
        if (targetType === 'track') data.trackId = eff.trackId;
        // Editor permission means write access via durable grant — those
        // require an authenticated uid by RTDB rule, so anonymous redeem
        // can't take the editor path. Pin requireAccount on so the redeem
        // CF gates anonymous callers up front instead of after-the-fact.
        if (permission === 'editor') data.requireAccount = true;
        if (usePassword && password) data.password = password;
        if (useExpiry && expiresAt) {
            const ms = new Date(expiresAt).getTime();
            if (Number.isFinite(ms) && ms > Date.now()) data.expiresAt = ms;
        }
        if (useMaxUses && maxUses > 0) data.maxUses = Number(maxUses);

        try {
            const fn = firebase.functions().httpsCallable('createShareLink');
            const res = await fn(data);
            setResultUrl(res?.data?.url || null);
        } catch (err) {
            setError(err?.message || "Couldn't create link.");
        } finally {
            setBusy(false);
        }
    };

    const copy = async () => {
        if (!resultUrl) return;
        try {
            await navigator.clipboard.writeText(resultUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        } catch (_e) { /* ignore */ }
    };

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div className="modal-card share-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-eyebrow eyebrow-share">
                    <FontAwesomeIcon icon={faLink} />
                    <span>NEW SHARE LINK</span>
                </div>
                <div className="modal-title">{eff?.name || 'Untitled'}</div>

                {!resultUrl ? (
                    <>

                        <div className="composer-permission-pair">
                            <button
                                type="button"
                                className={`composer-permission-card ${permission === 'view' ? 'is-selected' : ''}`}
                                onClick={() => setPermission('view')}
                                disabled={busy}
                            >
                                <span className="composer-permission-icon icon-blue">
                                    <FontAwesomeIcon icon={faEye} />
                                </span>
                                <div className="composer-permission-text">
                                    <div className="composer-permission-title">View only</div>
                                    <div className="composer-permission-sub">Listen & read; can't change anything</div>
                                </div>
                            </button>
                            <button
                                type="button"
                                className={`composer-permission-card ${permission === 'editor' ? 'is-selected' : ''}`}
                                onClick={() => setPermission('editor')}
                                disabled={busy}
                            >
                                <span className="composer-permission-icon icon-share">
                                    <FontAwesomeIcon icon={faPenNib} />
                                </span>
                                <div className="composer-permission-text">
                                    <div className="composer-permission-title">Editor</div>
                                    <div className="composer-permission-sub">Add & edit tracks, lyrics, ideas, art</div>
                                </div>
                            </button>
                        </div>

                        <div className="share-row">
                            <span className="share-row-label">
                                <span className={`share-row-icon ${allowDownloads ? 'is-on' : ''}`}>
                                    <FontAwesomeIcon icon={faDownload} />
                                </span>
                                Allow downloads
                            </span>
                            <Toggle
                                checked={allowDownloads}
                                disabled={busy}
                                onChange={(e) => setAllowDownloads(e.target.checked)}
                            />
                        </div>

                        <div className="share-row">
                            <span className="share-row-label">
                                <span className={`share-row-icon ${usePassword ? 'is-locked' : ''}`}>
                                    <FontAwesomeIcon icon={faKey} />
                                </span>
                                Require password
                            </span>
                            <Toggle
                                checked={usePassword}
                                accent="purple"
                                disabled={busy}
                                onChange={(e) => setUsePassword(e.target.checked)}
                            />
                        </div>
                        {usePassword && (
                            <div className="share-row indent">
                                <span className="share-row-label">Password</span>
                                <div className="share-password-input">
                                    <input
                                        className="share-text-input"
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        disabled={busy}
                                        placeholder="••••••"
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        className="share-password-eye"
                                        onClick={() => setShowPassword((v) => !v)}
                                        disabled={busy}
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                        title={showPassword ? 'Hide password' : 'Show password'}
                                    >
                                        <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="share-row">
                            <span className="share-row-label">
                                <span className={`share-row-icon ${useExpiry ? 'is-warn' : ''}`}>
                                    <FontAwesomeIcon icon={faClock} />
                                </span>
                                Expires
                            </span>
                            <Toggle
                                checked={useExpiry}
                                disabled={busy}
                                onChange={(e) => setUseExpiry(e.target.checked)}
                            />
                        </div>
                        {useExpiry && (
                            <div className="share-row indent">
                                <span className="share-row-label">When</span>
                                <input
                                    className="share-text-input"
                                    type="datetime-local"
                                    value={expiresAt}
                                    disabled={busy}
                                    onChange={(e) => setExpiresAt(e.target.value)}
                                />
                            </div>
                        )}

                        <div className="share-row">
                            <span className="share-row-label">
                                <span className={`share-row-icon ${useMaxUses ? 'is-info' : ''}`}>
                                    <FontAwesomeIcon icon={faHashtag} />
                                </span>
                                Limit uses
                            </span>
                            <Toggle
                                checked={useMaxUses}
                                accent="blue"
                                disabled={busy}
                                onChange={(e) => setUseMaxUses(e.target.checked)}
                            />
                        </div>
                        {useMaxUses && (
                            <div className="share-row indent">
                                <span className="share-row-label">Max uses</span>
                                <input
                                    className="share-text-input"
                                    type="number"
                                    min="1"
                                    value={maxUses}
                                    disabled={busy}
                                    onChange={(e) => setMaxUses(e.target.value)}
                                />
                            </div>
                        )}

                        {error && <div className="new-project-error">{error}</div>}

                        <div className="new-project-actions">
                            <button className="btn-ghost" onClick={onBack} disabled={busy}>
                                <FontAwesomeIcon icon={faCircleArrowLeft} /> Back
                            </button>
                            <button className="btn-primary btn-glow" onClick={create} disabled={busy}>
                                <FontAwesomeIcon icon={faWandMagicSparkles} />
                                <span>{busy ? 'Generating…' : 'Generate link'}</span>
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="composer-success">
                            <div className="composer-success-icon">
                                <FontAwesomeIcon icon={faCircleCheck} />
                            </div>
                            <div className="composer-success-title">Link ready</div>
                            <div className="composer-success-sub">
                                Anyone with this link can open it
                                {usePassword ? ' (with the password)' : ''}.
                            </div>
                            <div className="share-link-preview">
                                <FontAwesomeIcon icon={faLink} className="share-link-preview-icon" />
                                <code>{resultUrl}</code>
                                <button className={`btn-ghost ${copied ? 'is-copied' : ''}`} onClick={copy}>
                                    <FontAwesomeIcon icon={copied ? faCircleCheck : faCopy} />
                                    {copied ? ' Copied' : ' Copy'}
                                </button>
                            </div>
                        </div>
                        <div className="new-project-actions">
                            <button className="btn-ghost" onClick={onBack}>Back</button>
                            <button className="btn-primary" onClick={onClose}>Done</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ShareLinkComposer;
